/* eslint-disable no-case-declarations */
/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, setDoc, updateDoc, arrayUnion } from 'firebase/firestore'
import { firestore, auth, storage } from '../../config/firebase'
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from 'firebase/storage'
import { arrayOfTimesGeneral } from '../../components/pages/Appointments/timeLines'
import { autoGenerateaptForm } from './pthelper'
import { addNewAppointmentFromFile, deleteAppointment } from '../appointments/appointmentSlice'

let patientsUnsubscribe = null;
let patientsSubscribed = false;

export const startPatientsListener = createAsyncThunk(
  'patients/startListener',
  async (_, { dispatch }) => {
    if (patientsSubscribed) return;
    patientsSubscribed = true;

    const ref = collection(firestore, 'patients');
    patientsUnsubscribe = onSnapshot(ref, (snapshot) => {
      console.log(`👥 Processing ${snapshot.docs.length} patients...`)
      
      // PERFORMANCE OPTIMIZATION: For patients, use requestAnimationFrame for smooth processing
      const docs = snapshot.docs;
      const batchSize = 100; // Larger batch size for simpler patient data
      
      if (docs.length <= batchSize) {
        // Small dataset - process immediately
        const patientsData = docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by createdAt descending (newest first)
        const sortedPatients = patientsData.sort((a, b) => 
          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        
        console.log(`👥 Patients loaded: ${sortedPatients.length} items`)
        dispatch(setPatients(sortedPatients));
      } else {
        // Large dataset - use requestAnimationFrame for smooth processing
        let allPatients = [];
        let currentIndex = 0;
        
        const processNextBatch = () => {
          const endIndex = Math.min(currentIndex + batchSize, docs.length);
          const batchDocs = docs.slice(currentIndex, endIndex);
          
          const batchData = batchDocs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }));
          
          allPatients.push(...batchData);
          currentIndex = endIndex;
          
          if (currentIndex < docs.length) {
            // Process next batch on next animation frame
            requestAnimationFrame(processNextBatch);
          } else {
            // All batches processed - sort and dispatch
            const sortedPatients = allPatients.sort((a, b) => 
              new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
            );
            console.log(`👥 Patients loaded (batch processed): ${sortedPatients.length} items`)
            dispatch(setPatients(sortedPatients));
          }
        };
        
        requestAnimationFrame(processNextBatch);
      }
    });
  }
);

export const stopPatientsListener = createAsyncThunk(
  'patients/stopListener',
  async () => {
    if (patientsUnsubscribe) patientsUnsubscribe();
    patientsUnsubscribe = null;
    patientsSubscribed = false;
  }
);

// Keep the old function name for backward compatibility
export const getAllPatients = startPatientsListener;

export const addNewPatient = createAsyncThunk('auth/addNewPatient', async (data, ThunkAPI) => {
  const { close, setterFn, operationData, room, ...rest } = data
  try {
    const operationCollection = collection(firestore, 'patients')
    const optimisticData = {
      ...rest,
      creator: auth.currentUser.uid,
      room: room || '', // Ensure room is saved in the patient document
      createdAt: new Date().toISOString(), // Add creation timestamp
      // Add medical examination fields
      rtg_pa_pulmo: rest.rtg_pa_pulmo || '',
      ekg: rest.ekg || '',
      ta: rest.ta || '',
      spo2: rest.spo2 || '',
      dg: rest.dg || ''
    }
    const realData = await addDoc(operationCollection, optimisticData)
    
    // Create complete patient object with ID to pass to the setter function
    const newPatientWithId = {
      id: realData.id,
      ...optimisticData
    }
    
    if (operationData) {
      // Support both legacy operationz and new services array
      const { operationz, services, teeth_list, ...rest } = operationData;
      const patientId = realData.id;
      // If services array exists, treat each as a new operation
      if (Array.isArray(services) && services.length > 0) {
        for (const serviceName of services) {
          if (serviceName && patientId) {
            const patientName = data?.full_name || 'Anonymous';
            // Create a minimal operation object
            const operation = { id: (Math.random() + 1).toString(36).substring(2), op_name: serviceName, category: data.category || 'stomatology' };
            const aptObj = autoGenerateaptForm({
              operation,
              patientId,
              patientName,
              multiplier: 1,
              mainIssue: rest.main_issue,
              paidOperation: false,
              appointment_date: data.appointment_date,
              appointment_time: data.appointment_time,
              doctor_id: operationData?.doctor_id,
              doctor_name: operationData?.doctor_name
            });
            await ThunkAPI.dispatch(addNewAppointmentFromFile({
              ...aptObj,
              additionalPayload: {
                ...rest,
                teeth_list: teeth_list || []
              },
              close
            }));
          }
        }
      }
      // ... legacy operationz logic remains for backward compatibility
      if (Array.isArray(operationz) && operationz.length > 0) {
        for (const operation of operationz) {
          if (operation && patientId && operation.id) {
            const patientName = data?.full_name || 'Anonymous';
            const aptObj = autoGenerateaptForm({
              operation,
              patientId,
              patientName,
              multiplier: operation.multiplier,
              mainIssue: rest.main_issue,
              paidOperation: operation.paidOperation,
              appointment_date: data.appointment_date,
              appointment_time: data.appointment_time,
              doctor_id: operationData?.doctor_id,
              doctor_name: operationData?.doctor_name
            });
            await ThunkAPI.dispatch(addNewAppointmentFromFile({
              ...aptObj,
              additionalPayload: {
                ...rest,
                teeth_list: teeth_list || []
              },
              close
            }));
          }
        }
      }
    }
    
    // Call the setter function with the complete patient object including ID
    setterFn && setterFn(newPatientWithId)
    
    return optimisticData
  } catch (error) {
    console.log(error)
    throw new Error(error.message) // Handle any errors
  } finally {
    close && close()
  }
})

export const updatePatient = createAsyncThunk('auth/updatePatient', async (data) => {
  const { close, id, operations, ...rest } = data
  try {
    const operationRef = doc(firestore, 'patients', id)
    
    // If operations array is provided, update it
    if (operations) {
      await updateDoc(operationRef, { operations })
    }
    
    // Update any other fields including medical examinations
    if (Object.keys(rest).length > 0) {
      const updateData = {
        ...rest,
        rtg_pa_pulmo: rest.rtg_pa_pulmo || '',
        ekg: rest.ekg || '',
        ta: rest.ta || '',
        spo2: rest.spo2 || '',
        dg: rest.dg || ''
      }
      await updateDoc(operationRef, updateData)
    }
    
    return data
  } catch (error) {
    console.error('Error updating patient:', error)
    throw new Error(error.message)
  } finally {
    close && close()
  }
})

export const uploadPatientFile = createAsyncThunk(
  'auth/uploadPatientFile',
  async ({ file, type, profileId, opinitId, onProgress }, thunkAPI) => {
    const { rejectWithValue, dispatch, getState } = thunkAPI
    const path = `patients/${profileId}/${!opinitId ? type : opinitId + '/' + type}/${file.name}`
    try {
      const fileRef = ref(storage, path)
      const uploadTask = uploadBytesResumable(fileRef, file)

      // eslint-disable-next-line no-undef
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            if (onProgress) onProgress(progress)
          },
          (error) => {
            console.error('Error uploading file:', error)
            reject(rejectWithValue(error.message))
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
              const uploadObject = {
                fileName: file.name,
                url: downloadURL,
                type,
                opinitId: opinitId || '',
                path,
                date: new Date().toISOString()
              }

              const state = getState()
              const patients = state.patients.patients
              const index = patients.findIndex((el) => el.id === profileId)
              if (index === -1) throw new Error('Patient not found')
              const patient = patients[index]
              let prevAttachements = patient?.attachements || []
              if (type === 'pfp') {
                const anyprevPfp = prevAttachements.filter((el) => el.type === 'pfp')
                for (const pfp of anyprevPfp) {
                  try {
                    await deleteObject(ref(storage, pfp.path))
                  } catch (error) {
                    console.error('Error deleting previous pfp:', error)
                  }
                }
                prevAttachements = prevAttachements.filter((el) => el.type !== 'pfp')
              }
              const updatedFiles = [uploadObject, ...(prevAttachements || [])]
              console.log({
                id: profileId,
                attachements: updatedFiles
              })
              await dispatch(
                updatePatient({
                  id: profileId,
                  attachements: updatedFiles
                })
              )

              resolve(uploadObject) // Resolve with the upload object if everything succeeds
            } catch (error) {
              console.error('Error updating patient files:', error)
              reject(rejectWithValue(error.message))
            }
          }
        )
      })
    } catch (error) {
      console.error('Error uploading file:', error)
      return rejectWithValue(error.message)
    }
  }
)

export const deletePatientFile = createAsyncThunk('auth/deletePatientFile', async ({ path, profileId }, ThunkAPI) => {
  try {
    const fileRef = ref(storage, path)
    await deleteObject(fileRef)

    const state = ThunkAPI.getState()
    const patients = state.patients.patients
    const index = patients.findIndex((el) => el.id === profileId)
    if (index === -1) throw new Error('Patient not found')

    const patient = patients[index]
    const updatedFiles = patient.attachements.filter((el) => el.path !== path)

    await ThunkAPI.dispatch(
      updatePatient({
        id: profileId,
        attachements: updatedFiles
      })
    )

    return path
  } catch (error) {
    console.error('Error deleting file:', error)
    throw new Error(error.message)
  }
})

export const deletePatient = createAsyncThunk('auth/deletePatient', async (data, ThunkAPI) => {
  const { close, id } = data
  try {
    // Delete all appointments for this patient
    const state = ThunkAPI.getState()
    const allAppointments = state.appointments.appointments || []
    const patientAppointments = allAppointments.filter(app => app.patient_id === id)
    for (const apt of patientAppointments) {
      // Remove from calendar (full delete)
      await ThunkAPI.dispatch(deleteAppointment({ id: apt.id, keepInPatientRecord: false }))
    }
    const operationRef = doc(firestore, 'patients', id)
    await deleteDoc(operationRef)
    return id
  } catch (error) {
    console.log(error)
    throw new Error(error.message) // Handle any errors
  } finally {
    close && close()
  }
})

export const addAptToPatient = createAsyncThunk('patients/addAptToPatient', async ({ id, operation_id, date, aptId, category, updating, additionalPayload, force_update }, { dispatch, getState }) => {
  const { patients } = getState().patients
  const patient = patients.find((pat) => pat.id === id)
  if (!patient) {
    return
  }

  // Prepare the payload with the standard fields
  const payload = {
    id,
    operation_id,
    appointment_id: aptId,
    init_date: date
  }

  // Add fields to payload if provided in additionalPayload
  if (additionalPayload) {
    if (additionalPayload.teeth_list) {
      payload.teeth_list = additionalPayload.teeth_list;
    }
    if (additionalPayload.room) {
      payload.room = additionalPayload.room;
    }
    if (additionalPayload.feedback) {
      payload.feedback = additionalPayload.feedback;
    }
    if (additionalPayload.examination) {
      payload.examination = additionalPayload.examination;
    }
    if (additionalPayload.therapy) {
      payload.therapy = additionalPayload.therapy;
    }
    if (additionalPayload.service_cost !== undefined) {
      payload.service_cost = additionalPayload.service_cost;
    }
    if (additionalPayload.patient_pay !== undefined) {
      payload.patient_pay = additionalPayload.patient_pay;
    }
    if (additionalPayload.difference !== undefined) {
      payload.difference = additionalPayload.difference;
    }
    // Include additional legacy pricing fields if present
    if (additionalPayload.price !== undefined) {
      payload.price = additionalPayload.price;
    }
    if (additionalPayload.paid_amount !== undefined) {
      payload.paid_amount = additionalPayload.paid_amount;
    }
    if (additionalPayload.is_paid !== undefined) {
      payload.is_paid = additionalPayload.is_paid;
    }
  }

  try {
    const patientRef = doc(firestore, 'patients', id);
    
    // Handle forced update or normal updating
    if ((updating && patient?.operations?.length) || force_update) {
      let updatedOperations = patient.operations;
      if (patient?.operations?.length) {
        updatedOperations = patient.operations.map((op) => {
          // Handle latest operation or exact operation with specific ID
          if ((op.operation_id === operation_id && op.latest) || 
              (op.operation_id === operation_id && op.init_date === updating) ||
              (op.appointment_id === aptId)) {
            
            return {
              ...op,
              latest: true,
              init_date: date || op.init_date,
              teeth_list: additionalPayload?.teeth_list || op.teeth_list || [],
              room: additionalPayload?.room || op.room || '',
              feedback: additionalPayload?.feedback || op.feedback || '',
              examination: additionalPayload?.examination || op.examination || '',
              therapy: additionalPayload?.therapy || op.therapy || '',
              service_cost: additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : op.service_cost || 0,
              patient_pay: additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : op.patient_pay || 0,
              difference: additionalPayload?.difference !== undefined ? additionalPayload.difference : op.difference || 0,
              // Include legacy fields for compatibility
              price: additionalPayload?.price !== undefined ? additionalPayload.price : 
                     (additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : op.price || op.service_cost || 0),
              paid_amount: additionalPayload?.paid_amount !== undefined ? additionalPayload.paid_amount : 
                          (additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : op.paid_amount || op.patient_pay || 0),
              is_paid: additionalPayload?.is_paid !== undefined ? additionalPayload.is_paid : 
                      ((additionalPayload?.patient_pay || op.patient_pay || 0) >= (additionalPayload?.service_cost || op.service_cost || 0))
            }
          }
          return op;
        });
      }
      
      // Update in Firebase
      await updateDoc(patientRef, {
        operations: updatedOperations
      });
      
      const data = {
        ...patient,
        operations: updatedOperations
      };
      
      return data;
    } else {
      // Check if this service already exists for this patient
      const existingService = patient.operations?.find(op => 
        op.operation_id === operation_id && 
        op.appointment_id === aptId
      );

      if (existingService) {
        // If service exists, update it instead of adding a new one
        const updatedOperations = patient.operations.map(op => 
          (op.operation_id === operation_id && op.appointment_id === aptId) ? {
            ...op,
            latest: true,
            init_date: date || op.init_date,
            teeth_list: additionalPayload?.teeth_list || op.teeth_list || [],
            room: additionalPayload?.room || op.room || '',
            feedback: additionalPayload?.feedback || op.feedback || '',
            examination: additionalPayload?.examination || op.examination || '',
            therapy: additionalPayload?.therapy || op.therapy || '',
            service_cost: additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : op.service_cost || 0,
            patient_pay: additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : op.patient_pay || 0,
            difference: additionalPayload?.difference !== undefined ? additionalPayload.difference : op.difference || 0,
            price: additionalPayload?.price !== undefined ? additionalPayload.price : 
                   (additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : op.price || op.service_cost || 0),
            paid_amount: additionalPayload?.paid_amount !== undefined ? additionalPayload.paid_amount : 
                        (additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : op.paid_amount || op.patient_pay || 0),
            is_paid: additionalPayload?.is_paid !== undefined ? additionalPayload.is_paid : 
                    ((additionalPayload?.patient_pay || op.patient_pay || 0) >= (additionalPayload?.service_cost || op.service_cost || 0))
          } : op
        );

        await updateDoc(patientRef, {
          operations: updatedOperations
        });

        return {
          ...patient,
          operations: updatedOperations
        };
      }

      // For new operation, create operation object with all required fields
      const op = {
        operation_id,
        category,
        latest: true,
        init_date: date,
        appointment_id: aptId,
        teeth_list: additionalPayload?.teeth_list || [],
        room: additionalPayload?.room || '',
        feedback: additionalPayload?.feedback || '',
        examination: additionalPayload?.examination || '',
        therapy: additionalPayload?.therapy || '',
        service_cost: additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : 0,
        patient_pay: additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : 0,
        difference: additionalPayload?.difference !== undefined ? additionalPayload.difference : 0,
        // Include legacy fields
        price: additionalPayload?.price !== undefined ? additionalPayload.price : 
               (additionalPayload?.service_cost !== undefined ? additionalPayload.service_cost : 0),
        paid_amount: additionalPayload?.paid_amount !== undefined ? additionalPayload.paid_amount : 
                    (additionalPayload?.patient_pay !== undefined ? additionalPayload.patient_pay : 0),
        is_paid: additionalPayload?.is_paid !== undefined ? additionalPayload.is_paid : 
                ((additionalPayload?.patient_pay || 0) >= (additionalPayload?.service_cost || 0))
      };
      
      // Add to Firebase (using array union for atomic operation)
      await updateDoc(patientRef, {
        operations: arrayUnion(op),
        appointments: arrayUnion(aptId)
      });
      
      return {
        ...patient,
        operations: [...(patient.operations || []), op],
        appointments: [...(patient.appointments || []), aptId]
      };
    }
  } catch (error) {
    console.error("Error updating patient:", error);
    throw error;
  }
})

export const addOperationToPatient = createAsyncThunk('patients/addOperationToPatient', async ({ opId, patientId }, ThunkAPI) => {
  try {
    const state = ThunkAPI.getState()
    const patients = state.patients.patients // Access patients from the state

    const index = patients.findIndex((el) => el.id === patientId)
    if (index === -1) throw new Error('Patient not found')

    const patient = patients[index]
    const currentOps = patient.operations || []

    const updatedOps = [...currentOps, { operation_id: opId }]

    // Update patient appointments in Firestore
    await ThunkAPI.dispatch(updatePatient({ id: patientId, operations: updatedOps }))

    // Return the updated patient
    return { patientId, updatedOps }
  } catch (error) {
    console.error(error)
    throw error
  }
})

const patients = createSlice({
  name: 'patients',
  initialState: {
    patients: [],
    loading: false,
    error: null
  },
  reducers: {
    setPatients: (state, action) => {
      state.patients = action.payload;
      state.loading = false;
    },
    resetPatients: (state) => {
      patientsSubscribed = false;
      state.patients = [];
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(startPatientsListener.pending, (state) => {
        state.loading = true
      })
      .addCase(startPatientsListener.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
      })
      .addCase(startPatientsListener.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const { setPatients, resetPatients } = patients.actions
export default patients.reducer
